import { useList, useDelete, useNavigation, useOne, useCreate, useUpdate } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ArrowLeft, Save, Loader2 } from 'lucide-react';
import type { MeetingPackage } from '@natsepa/database';
import { BilingualField } from '../../components/BilingualField';
import { BilingualArrayField } from '../../components/BilingualArrayField';

const PACKAGE_TYPES = ['half-day', 'full-day', 'one-day', 'residential'] as const;

const TYPE_LABELS: Record<typeof PACKAGE_TYPES[number], string> = {
  'half-day': 'Half Day',
  'full-day': 'Full Day',
  'one-day': 'One Day',
  'residential': 'Residential',
};

export function MeetingPackagesList() {
  const { data, isLoading, error } = useList<MeetingPackage>({ resource: 'meeting-packages' });
  const { mutate: deleteOne } = useDelete();
  const { edit, create } = useNavigation();

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this meeting package?')) {
      deleteOne({ resource: 'meeting-packages', id });
    }
  };

  if (isLoading) return <div className="p-4">Loading...</div>;
  if (error) return (
    <div className="p-4"><div className="bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-red-800">Error loading meeting packages: {error.message}</p></div></div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Meeting Packages</h1>
        <button onClick={() => create('meeting-packages')} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors">
          <Plus className="w-5 h-5" /> Add Package
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.data.map((pkg) => (
              <tr key={pkg.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap"><div className="font-medium text-gray-900">{pkg.name}</div></td>
                <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">{TYPE_LABELS[pkg.type]}</span></td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{pkg.duration || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{pkg.price ? `IDR ${pkg.price.toLocaleString()}` : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => edit('meeting-packages', pkg.id)} className="p-2 text-gray-400 hover:text-gold-600 transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(pkg.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.data.length === 0 && <div className="p-8 text-center text-gray-500">No meeting packages found. Click "Add Package" to create one.</div>}
      </div>
    </div>
  );
}

// Create Meeting Package
export function MeetingPackagesCreate() {
  const navigate = useNavigate();
  const { mutate: createPackage, isPending } = useCreate();
  const [form, setForm] = useState({
    type: 'half-day' as typeof PACKAGE_TYPES[number],
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    duration: '',
    price: 0,
    minPax: 0,
    includes: [] as { en: string; id: string }[],
    noteEn: '',
    noteId: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nameId) { alert('Please fill in the name'); return; }
    createPackage(
      { resource: 'meeting-packages', values: { ...form, name: form.nameId || form.nameEn, description: form.descriptionId || form.descriptionEn } },
      { onSuccess: (data) => { const newId = data?.data?.id; navigate(newId ? `/meeting-packages/edit/${newId}` : '/meeting-packages'); } }
    );
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/meeting-packages')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Meeting Package</h1>
          <p className="text-sm text-gray-500 mt-1">Add a new meeting or conference package</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
          <BilingualField label="Name" valueEn={form.nameEn} valueId={form.nameId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })} required />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Package Type *</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as typeof PACKAGE_TYPES[number] })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent">
                {PACKAGE_TYPES.map((type) => (<option key={type} value={type}>{TYPE_LABELS[type]}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <input type="text" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="e.g., 4 hours, 1 day" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (IDR)</label>
              <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min. Pax</label>
              <input type="number" value={form.minPax} onChange={(e) => setForm({ ...form, minPax: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800"><strong>Note:</strong> You can add inclusions and more details after creating the package.</p>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/meeting-packages')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
            {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Create & Continue
          </button>
        </div>
      </form>
    </div>
  );
}

// Edit Meeting Package
export function MeetingPackagesEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useOne<MeetingPackage>({ resource: 'meeting-packages', id: id! });
  const { mutate: updatePackage, isPending } = useUpdate();

  const [form, setForm] = useState({
    type: 'half-day' as typeof PACKAGE_TYPES[number],
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    duration: '',
    price: 0,
    minPax: 0,
    includes: [] as { en: string; id: string }[],
    noteEn: '',
    noteId: '',
  });

  useEffect(() => {
    if (data?.data) {
      const pkg = data.data;
      const zipBi = (en: any[] | undefined, id: any[] | undefined) => {
        const e = Array.isArray(en) ? en : [];
        const d = Array.isArray(id) ? id : [];
        const len = Math.max(e.length, d.length);
        return Array.from({ length: len }, (_, i) => ({ en: e[i] || d[i] || '', id: d[i] || e[i] || '' }));
      };
      setForm({
        type: pkg.type || 'half-day',
        nameEn: (pkg as any).nameEn || (pkg as any).name_en || pkg.name || '',
        nameId: (pkg as any).nameId || (pkg as any).name_id || pkg.name || '',
        descriptionEn: (pkg as any).descriptionEn || (pkg as any).description_en || pkg.description || '',
        descriptionId: (pkg as any).descriptionId || (pkg as any).description_id || pkg.description || '',
        duration: pkg.duration || '',
        price: pkg.price || 0,
        minPax: pkg.minPax || 0,
        includes: zipBi((pkg as any).includesEn, (pkg as any).includesId),
        noteEn: (pkg as any).noteEn || (pkg as any).note_en || pkg.note || '',
        noteId: (pkg as any).noteId || (pkg as any).note_id || pkg.note || '',
      });
    }
  }, [data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updatePackage(
      { resource: 'meeting-packages', id: id!, values: form },
      { onSuccess: () => navigate('/meeting-packages') }
    );
  };

  if (isLoading) return <div className="p-4">Loading...</div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/meeting-packages')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Meeting Package</h1>
          <p className="text-sm text-gray-500 mt-1">{form.nameId || form.nameEn || 'Package details'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
          <BilingualField label="Name" valueEn={form.nameEn} valueId={form.nameId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })} required />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Package Type *</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as typeof PACKAGE_TYPES[number] })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent">
                {PACKAGE_TYPES.map((type) => (<option key={type} value={type}>{TYPE_LABELS[type]}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <input type="text" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="e.g., 4 hours, 1 day" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (IDR)</label>
              <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min. Pax</label>
              <input type="number" value={form.minPax} onChange={(e) => setForm({ ...form, minPax: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Description</h2>
          <BilingualField label="Full Description" valueEn={form.descriptionEn} valueId={form.descriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'descriptionEn' : 'descriptionId']: value })} multiline rows={4} />
          <BilingualField label="Note" valueEn={form.noteEn} valueId={form.noteId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'noteEn' : 'noteId']: value })} multiline rows={2} placeholder="Additional notes or terms..." />
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">What's Included</h2>
          <BilingualArrayField label="Includes" items={form.includes} onChange={(includes) => setForm({ ...form, includes })} />
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/meeting-packages')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
            {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Changes
          </button>
        </div>
      </form>
    </div>
  );
}
